import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Standard Loading State for Commerce Index
 *
 * Consistent loading indicators across all pages:
 *
 *   <LoadingState />                        — full page spinner
 *   <LoadingState size="sm" />              — inline spinner
 *   <LoadingState message="Loading..." />   — spinner with message
 *   <LoadingState variant="skeleton" />     — skeleton cards
 */

export function LoadingSpinner({ size = 'md', message, className = '' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <Loader2 className={`${sizeClasses[size]} text-cyan-400 animate-spin`} />
      {message && <p className="text-sm text-zinc-400">{message}</p>}
    </div>
  );
}

export function LoadingPage({ message = 'Loading...' }) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <LoadingSpinner size="lg" message={message} />
    </div>
  );
}

export function LoadingSkeleton({ rows = 3, className = '' }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
          <div className="h-3 bg-zinc-800/60 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function LoadingCard({ count = 3, className = '' }) {
  return (
    <div className={`grid gap-4 md:grid-cols-${Math.min(count, 4)} ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
          <div className="h-3 bg-zinc-800 rounded w-1/3 mb-4" />
          <div className="h-8 bg-zinc-800/60 rounded w-2/3 mb-2" />
          <div className="h-3 bg-zinc-800/40 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

// Default export for simple usage: <LoadingState />
export default function LoadingState({ variant = 'spinner', ...props }) {
  switch (variant) {
    case 'page': return <LoadingPage {...props} />;
    case 'skeleton': return <LoadingSkeleton {...props} />;
    case 'card': return <LoadingCard {...props} />;
    default: return <LoadingSpinner size="md" {...props} />;
  }
}
